import { useCallback, useState } from 'react';

import { notifications } from '@mantine/notifications';

import * as Sentry from '@sentry/react';

import { vnextApiClient } from '@/services/vnext-api';
import { ExtractionSourceSet } from '@/types/extraction';
import { ExtractedCandidate } from '@/types/report-request';

export interface UseExtractReportRequestsResult {
  candidates: ExtractedCandidate[];
  needsHumanReview: boolean;
  overallReasoning: string | null;
  isExtracting: boolean;
  error: Error | null;
  /** Run extraction over a composable source set (body + messages + files).
   *  Returns the candidates so the caller can open the review panel. */
  extract: (sourceSet: ExtractionSourceSet) => Promise<ExtractedCandidate[]>;
  reset: () => void;
}

/**
 * Analyst-triggered, anywhere-in-conversation extraction. One `extract` call
 * sends the whole source set; candidates come back with provenance for review.
 */
export function useExtractReportRequests(
  enquiryId: string | undefined
): UseExtractReportRequestsResult {
  const [candidates, setCandidates] = useState<ExtractedCandidate[]>([]);
  const [needsHumanReview, setNeedsHumanReview] = useState(false);
  const [overallReasoning, setOverallReasoning] = useState<string | null>(null);
  const [isExtracting, setIsExtracting] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const reset = useCallback(() => {
    setCandidates([]);
    setNeedsHumanReview(false);
    setOverallReasoning(null);
    setError(null);
  }, []);

  const extract = useCallback(
    async (sourceSet: ExtractionSourceSet): Promise<ExtractedCandidate[]> => {
      if (!enquiryId) {
        return [];
      }
      setIsExtracting(true);
      setError(null);
      try {
        const result = await vnextApiClient.extractReportRequests(enquiryId, sourceSet);
        setCandidates(result.candidates);
        setNeedsHumanReview(Boolean(result.needs_human_review));
        setOverallReasoning(result.overall_reasoning ?? null);
        return result.candidates;
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        Sentry.captureException(e);
        setError(e);
        notifications.show({
          title: 'Extraction failed',
          message: e.message,
          color: 'red',
        });
        return [];
      } finally {
        setIsExtracting(false);
      }
    },
    [enquiryId]
  );

  return {
    candidates,
    needsHumanReview,
    overallReasoning,
    isExtracting,
    error,
    extract,
    reset,
  };
}
